/**
 * 应用层心跳与死连接判定（§7.9 / §7.10）。
 *
 * 浏览器 JS **拿不到** WebSocket 协议级的 ping/pong 帧，所以心跳必须走应用层消息。
 *
 * ★ 判死只看 `lastInboundAt`，**绝不看 `ws.readyState`**（§7.10）：
 *   iOS Safari 切后台约 30 秒后系统会回收 socket，但 `onclose` / `onerror`
 *   可能根本不触发、或者拖到回前台才触发，而这段时间里 `readyState` 仍然读到 1 (OPEN)。
 *   用 readyState 判活等于「连接早就死了，界面还显示在线，用户点什么都石沉大海」。
 *
 * ★ 任意入站消息都算心跳（不只是 pong）：牌桌一活跃，事件流本身就证明连接是通的，
 *   没必要非等那一条 pong。
 *
 * 首连时 2 秒内快打 3 次 ping（§7.8）：偏移估计要尽快收敛，否则第一手牌的倒计时是歪的。
 */
import { WS } from '@poker/shared'
import type { Millis } from '@poker/shared'

export type HeartbeatDeath = 'silence' | 'probeTimeout'

export interface HeartbeatTimers {
  now(): Millis
  setTimeout(fn: () => void, ms: number): unknown
  clearTimeout(handle: unknown): void
}

export const realTimers: HeartbeatTimers = {
  now: () => Date.now(),
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (h) => {
    globalThis.clearTimeout(h as ReturnType<typeof setTimeout>)
  },
}

export interface HeartbeatOptions {
  /** 发一条 `ping{nonce,t0}`。由 Transport 实现（心跳自己不碰 socket） */
  sendPing(nonce: string, t0: Millis): void
  /** 判死回调：调用方应当 `ws.close(4000)` 然后**立即**重连（不进退避，§7.9） */
  onDead(reason: HeartbeatDeath): void
  readonly intervalMs?: number
  readonly deadAfterMs?: number
  readonly timers?: HeartbeatTimers
  readonly makeNonce?: () => string
  /** 首连快打次数与间隔（§7.8 的「2s 内 3 次」） */
  readonly warmupPings?: number
  readonly warmupSpacingMs?: number
}

export interface Heartbeat {
  /** 连接建立（收到 welcome）后启动 */
  start(): void
  stop(): void
  /** 任意入站帧都要调；它同时会结束正在进行的探活 */
  noteInbound(at?: Millis): void
  /**
   * 主动探活（回前台时用，§7.10）：立刻发一条 ping，并在 `timeoutMs` 内
   * 没有任何入站消息就判死。回前台**必须**探活——不能假设 socket 还活着。
   */
  probe(opts?: { timeoutMs?: number; onTimeout?: () => void }): void
  probing(): boolean
  lastInboundAt(): Millis
  msSinceInbound(now?: Millis): number
  running(): boolean
}

/** 判死条件（纯函数，便于单测）。 */
export function isConnectionDead(now: Millis, lastInboundAt: Millis, deadAfterMs: number): boolean {
  return now - lastInboundAt > deadAfterMs
}

let nonceCounter = 0
function defaultNonce(): string {
  nonceCounter = (nonceCounter + 1) % 1_000_000
  return `hb-${String(nonceCounter)}-${Math.random().toString(36).slice(2, 8)}`
}

export function createHeartbeat(options: HeartbeatOptions): Heartbeat {
  const timers = options.timers ?? realTimers
  const intervalMs = options.intervalMs ?? WS.CLIENT_PING_INTERVAL_MS
  const deadAfterMs = options.deadAfterMs ?? WS.CLIENT_DEAD_AFTER_MS
  const makeNonce = options.makeNonce ?? defaultNonce
  const warmupPings = options.warmupPings ?? 3
  const warmupSpacing = options.warmupSpacingMs ?? 700

  let tickHandle: unknown = null
  let probeHandle: unknown = null
  let probeOnTimeout: (() => void) | null = null
  let lastInbound: Millis = 0
  let running = false
  let warmupLeft = 0

  const clearTick = (): void => {
    if (tickHandle !== null) {
      timers.clearTimeout(tickHandle)
      tickHandle = null
    }
  }

  const clearProbe = (): void => {
    if (probeHandle !== null) {
      timers.clearTimeout(probeHandle)
      probeHandle = null
    }
    probeOnTimeout = null
  }

  const ping = (): void => {
    options.sendPing(makeNonce(), timers.now())
  }

  /**
   * 下一次检查的时刻。除了常规心跳间隔，还要在「判死线」上单独安排一次：
   * 只按 15s 间隔轮询的话，35s 的判死线最坏要到第 50 秒才被发现——
   * 用户会多盯 15 秒的「一切正常」假象，而那条连接早就死了。
   */
  const schedule = (ms: number): void => {
    clearTick()
    const untilDead = deadAfterMs - (timers.now() - lastInbound) + 1
    tickHandle = timers.setTimeout(tick, Math.max(50, Math.min(ms, untilDead)))
  }

  const tick = (): void => {
    tickHandle = null
    if (!running) return
    const now = timers.now()
    if (isConnectionDead(now, lastInbound, deadAfterMs)) {
      // 先停表再回调：onDead 里通常会 close + 重连，重连会 start() 出新的定时器
      stop()
      options.onDead('silence')
      return
    }
    ping()
    if (warmupLeft > 0) {
      warmupLeft -= 1
      schedule(warmupLeft > 0 ? warmupSpacing : intervalMs)
      return
    }
    schedule(intervalMs)
  }

  function stop(): void {
    running = false
    warmupLeft = 0
    clearTick()
    clearProbe()
  }

  return {
    start() {
      stop()
      running = true
      lastInbound = timers.now()
      warmupLeft = Math.max(0, warmupPings - 1)
      // 第一条 ping 立刻发：偏移估计越早收敛越好
      ping()
      schedule(warmupLeft > 0 ? warmupSpacing : intervalMs)
    },
    stop,
    noteInbound(at) {
      lastInbound = at ?? timers.now()
      // 有入站就说明连接活着，探活到此为止
      clearProbe()
    },
    probe(opts = {}) {
      const timeoutMs = opts.timeoutMs ?? WS.FG_PROBE_TIMEOUT_MS
      clearProbe()
      probeOnTimeout = opts.onTimeout ?? null
      ping()
      probeHandle = timers.setTimeout(() => {
        probeHandle = null
        const cb = probeOnTimeout
        probeOnTimeout = null
        // 探活超时 = 这条连接已经是半开的（对端消失但 TCP 没 FIN），只能硬重连
        if (cb !== null) cb()
        else options.onDead('probeTimeout')
      }, timeoutMs)
    },
    probing: () => probeHandle !== null,
    lastInboundAt: () => lastInbound,
    msSinceInbound: (now) => (now ?? timers.now()) - lastInbound,
    running: () => running,
  }
}
